// The look of an experiment: colours (in rel, with an sRGB swatch) and visualTools'
// chromatic edge jitter, including the cone-isolating L-M pair.

import { useState } from "react";
import { api } from "../api";
import { errorText } from "../hooks";
import { srgbEotf, srgbOetf } from "../lib/display";
import { CONE_AXIS_LABEL, JITTER_AXES, JITTER_UNIT_DEFAULTS, type ConeResult, type EdgeJitter, type RGB } from "../lib/protocol";
import { Help } from "./Help";

const hex2 = (n: number) => Math.round(Math.min(1, Math.max(0, n)) * 255).toString(16).padStart(2, "0");
export const relToHex = (v: RGB) => `#${hex2(srgbOetf(v[0]))}${hex2(srgbOetf(v[1]))}${hex2(srgbOetf(v[2]))}`;
const hexToRel = (h: string): RGB => [1, 3, 5].map((i) => +srgbEotf(parseInt(h.slice(i, i + 2), 16) / 255).toFixed(4)) as RGB;

/** An RGB colour in rel, with a swatch (sRGB) to pick from and the exact numbers. */
export function RgbField({ label, value, onChange }: { label: string; value: RGB; onChange: (v: RGB) => void }) {
  const setC = (i: number, v: string) => {
    const n = Math.min(1, Math.max(0, Number(v)));
    if (!Number.isFinite(n)) return;
    const next = [...value] as RGB;
    next[i] = n;
    onChange(next);
  };
  return (
    <fieldset>
      <legend>{label}</legend>
      <div className="row" style={{ alignItems: "center", gap: "0.4rem" }}>
        <input type="color" aria-label={`${label} colour`} value={relToHex(value)} onChange={(e) => onChange(hexToRel(e.target.value))} />
        {["R", "G", "B"].map((ch, i) => (
          <label key={ch} style={{ flexDirection: "row", gap: "0.2rem" }}>{ch}
            <input type="number" step="0.01" min="0" max="1" style={{ width: "4.5rem" }} aria-label={`${label} ${ch}`}
              value={value[i]} onChange={(e) => setC(i, e.target.value)} />
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** visualTools' chromatic edge jitter for one test. */
export function JitterEditor({ title, value, background, onChange }: {
  title: string; value: EdgeJitter; background: RGB; onChange: (v: EdgeJitter) => void;
}) {
  const j = value;
  const cone = j.colour_source === "cone_isolating";
  const set = (patch: Partial<EdgeJitter>) => onChange({ ...j, ...patch });
  const unitLabel = j.unit === "stroke" ? "strokes" : "px";
  const n = (v: string, f: number) => { const x = Number(v); return Number.isFinite(x) ? x : f; };
  return (
    <fieldset>
      <legend>{title}</legend>
      <label style={{ flexDirection: "row", gap: "0.3rem" }}>
        <input type="checkbox" checked={j.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        jittered colour blobs along the E's outline</label>
      {j.enabled && (
        <>
          <div className="row">
            <label><span>Colours<Help k="jitter.colours" ctx={{ jitter: j }} /></span>
              <select aria-label={`${title} jitter colours`} value={cone ? CONE_AXIS_LABEL : JITTER_AXES[j.axis] ? j.axis : "Custom"} onChange={(e) => {
                const v = e.target.value;
                const preset = JITTER_AXES[v];
                if (v === CONE_AXIS_LABEL) set({ colour_source: "cone_isolating", axis: "cone-isolating +L-M / -L+M" });
                else set(preset ? { colour_source: "preset", axis: v, color1_rgb_rel: [...preset[0]] as RGB, color2_rgb_rel: [...preset[1]] as RGB }
                  : { colour_source: "preset", axis: "Custom" });
              }}>
                <option value={CONE_AXIS_LABEL}>{CONE_AXIS_LABEL}</option>
                {Object.keys(JITTER_AXES).map((k) => <option key={k} value={k}>{k} (display primaries)</option>)}
                <option value="Custom">Custom</option>
              </select>
            </label>
            <label><span>Size in<Help k="jitter.unit" ctx={{ jitter: j }} /></span>
              <select aria-label={`${title} jitter units`} value={j.unit}
                onChange={(e) => { const u = e.target.value as EdgeJitter["unit"]; set({ unit: u, ...JITTER_UNIT_DEFAULTS[u] }); }}>
                <option value="stroke">E strokes (scales with the letter)</option>
                <option value="px">device pixels (fixed)</option>
              </select>
            </label>
            <label><span>Inside the letter ({unitLabel})<Help k="jitter.inner_width" ctx={{ jitter: j }} /></span><input type="number" step="0.05" min="0" value={j.inner_width}
              onChange={(e) => set({ inner_width: n(e.target.value, j.inner_width) })} /></label>
            <label><span>Outside the letter ({unitLabel})<Help k="jitter.outer_width" ctx={{ jitter: j }} /></span><input type="number" step="0.05" min="0" value={j.outer_width}
              onChange={(e) => set({ outer_width: n(e.target.value, j.outer_width) })} /></label>
            <label><span>Blob size ({unitLabel})<Help k="jitter.blob_size" ctx={{ jitter: j }} /></span><input type="number" step="0.05" min="0.01" value={j.blob_size}
              onChange={(e) => set({ blob_size: n(e.target.value, j.blob_size) })} /></label>
          </div>
          <div className="row">
            <label><span>Density (0–1)<Help k="jitter.density" ctx={{ jitter: j }} /></span><input type="number" step="0.05" min="0" max="1" value={j.density}
              onChange={(e) => set({ density: n(e.target.value, j.density) })} /></label>
            <label><span>Re-draw every (frames)<Help k="jitter.update_frames" ctx={{ jitter: j }} /></span><input type="number" step="1" min="1" max="120" value={j.update_frames}
              onChange={(e) => set({ update_frames: Math.max(1, Math.round(n(e.target.value, j.update_frames))) })} /></label>
            <label><span>Intensity (mix 0–1)<Help k="jitter.intensity" ctx={{ jitter: j }} /></span><input type="number" step="0.05" min="0" max="1" value={j.intensity}
              onChange={(e) => set({ intensity: n(e.target.value, j.intensity) })} /></label>
            <label><span>Strength<Help k="jitter.link_to_contrast" ctx={{ jitter: j }} /></span>
              <select aria-label={`${title} jitter strength`} value={j.link_to_contrast ? "linked" : "fixed"}
                onChange={(e) => set({ link_to_contrast: e.target.value === "linked" })}>
                <option value="linked">linked: fades with the letter's contrast</option>
                <option value="fixed">fixed: full strength at any contrast</option>
              </select>
            </label>
          </div>
          {cone && <ConeSettings title={title} j={j} background={background} set={set} />}
          {!cone && !JITTER_AXES[j.axis] && (
            <div className="row">
              <RgbField label="Jitter colour 1" value={j.color1_rgb_rel} onChange={(v) => set({ color1_rgb_rel: v })} />
              <RgbField label="Jitter colour 2" value={j.color2_rgb_rel} onChange={(v) => set({ color2_rgb_rel: v })} />
            </div>
          )}
        </>
      )}
    </fieldset>
  );
}

const pct = (v: number) => `${v >= 0 ? "+" : ""}${(100 * v).toFixed(1)}%`;

/** The cone-isolating pair: +L-M and M vs L (-L+M), S unchanged, computed by the server for
 *  each session's display (its EDID primaries, else sRGB) and this observer. */
function ConeSettings({ title, j, background, set }: {
  title: string; j: EdgeJitter; background: RGB; set: (patch: Partial<EdgeJitter>) => void;
}) {
  const o = j.cone_observer;
  const [preview, setPreview] = useState<ConeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const setO = (patch: Partial<typeof o>) => { setPreview(null); set({ cone_observer: { ...o, ...patch } }); };
  const n = (v: string, f: number) => { const x = Number(v); return Number.isFinite(x) ? x : f; };
  const look = async () => {
    setError(null);
    try {
      setPreview(await api.conePreview({ observer: o, mode: j.cone_mode, center: j.cone_center, background_rgb_rel: background }));
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <div className="cone-settings">
      <div className="row">
        <label><span>Direction<Help k="jitter.cone_mode" ctx={{ jitter: j }} /></span>
          <select aria-label={`${title} cone direction`} value={j.cone_mode}
            onChange={(e) => { setPreview(null); set({ cone_mode: e.target.value as EdgeJitter["cone_mode"] }); }}>
            <option value="cone_contrast">equal and opposite L and M cone contrast (S unchanged)</option>
            <option value="isoluminant">isoluminant L-M, the DKL axis (S and luminance unchanged)</option>
          </select>
        </label>
        <label><span>Centred on<Help k="jitter.cone_center" ctx={{ jitter: j }} /></span>
          <select aria-label={`${title} cone centre`} value={j.cone_center}
            onChange={(e) => { setPreview(null); set({ cone_center: e.target.value as EdgeJitter["cone_center"] }); }}>
            <option value="mid_grey">mid-grey (the most room: largest contrast)</option>
            <option value="background">the background (blobs differ from it only in L vs M)</option>
          </select>
        </label>
        <label><span>Cone fundamentals<Help k="jitter.cone_observer" ctx={{ jitter: j }} /></span>
          <select aria-label={`${title} cone fundamentals`} value={o.fundamentals}
            onChange={(e) => setO({ fundamentals: e.target.value as typeof o.fundamentals })}>
            <option value="neitz">Neitz photopigment template</option>
            <option value="stockman_sharpe_2">Stockman &amp; Sharpe 2°</option>
          </select>
        </label>
      </div>
      {o.fundamentals === "neitz" && (
        <div className="row">
          <label>L peak (nm)<input type="number" step="0.5" value={o.l_peak} onChange={(e) => setO({ l_peak: n(e.target.value, o.l_peak) })} /></label>
          <label>M peak (nm)<input type="number" step="0.5" value={o.m_peak} onChange={(e) => setO({ m_peak: n(e.target.value, o.m_peak) })} /></label>
          <label>S peak (nm)<input type="number" step="0.5" value={o.s_peak} onChange={(e) => setO({ s_peak: n(e.target.value, o.s_peak) })} /></label>
          <label>Observer age<input type="number" min="1" max="100" value={o.age} onChange={(e) => setO({ age: Math.round(n(e.target.value, o.age)) })} /></label>
          <label>Macular pigment (× standard)<input type="number" step="0.1" min="0" max="3" value={o.macular_density_weight}
            onChange={(e) => setO({ macular_density_weight: n(e.target.value, o.macular_density_weight) })} /></label>
          <label>Lens
            <select aria-label={`${title} lens model`} value={o.lens_model} onChange={(e) => setO({ lens_model: e.target.value as typeof o.lens_model })}>
              <option value="stockman">Stockman</option>
              <option value="pokorny">Pokorny/Smith/Lutze (age)</option>
              <option value="vannorren">van Norren &amp; Vos (age)</option>
              <option value="stiles_raw">Stiles</option>
            </select>
          </label>
        </div>
      )}
      <div className="row" style={{ alignItems: "center" }}>
        <button className="secondary" onClick={() => void look()}>Preview on an sRGB display</button>
        {preview && (
          <span className="small">
            <span className="swatch" style={{ background: relToHex(preview.plus_l_minus_m_rgb_rel) }} /> +L−M: L {pct(preview.cone_contrast_plus.L)},
            M {pct(preview.cone_contrast_plus.M)}, S {pct(preview.cone_contrast_plus.S)}{preview.cone_contrast_plus.luminance !== null && `, luminance ${pct(preview.cone_contrast_plus.luminance)}`}
            {" · "}<span className="swatch" style={{ background: relToHex(preview.minus_l_plus_m_rgb_rel) }} /> M vs L: L {pct(preview.cone_contrast_minus.L)},
            M {pct(preview.cone_contrast_minus.M)}
            {preview.l_minus_m_contrast === 0 && <span className="warn"> — no room around this centre (a white or black centre cannot be modulated; use mid-grey or a darker background)</span>}
          </span>
        )}
        {error && <span className="error small">{error}</span>}
      </div>
      <p className="small muted">The colours are worked out for each session's own display, from its EDID primaries (sRGB if it has no EDID),
        at the largest amplitude its gamut allows. Keep intensity 1 and strength “fixed” for blobs exactly at those colours; with the
        background as the centre, a lower or linked intensity scales the L vs M contrast down without adding S or luminance.</p>
    </div>
  );
}
